import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ActionIconButton, Pagination } from '@onesource/components';
import {
  EReviewSubmissionStatus,
  REVIEW_EXPORT_MAX_ROWS,
  type TReviewFacetOption,
  EReviewDueState,
  EReviewMissingProgress,
  type TReviewMissingForm,
  type TReviewMissingItem,
  type TReviewReminderRecipient,
  type TReviewTimeState,
} from '@onesource/schemas';
import {
  useDownloadReviewExport,
  useGetReviewFacets,
  useGetReviewMissing,
  useGetReviewMissingFacets,
  useGetReviewQueue,
  useGetSpace,
  useRemindReviewStudents,
} from '@onesource/services';
import { useNavigate } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import { FC, useEffect, useRef, useState } from 'react';
import {
  MdFileDownload,
  MdFilterAltOff,
  MdNotificationsActive,
  MdOutlineAssignment,
  MdOutlineEventBusy,
  MdOutlineFlag,
  MdOutlineFolder,
  MdOutlineInbox,
  MdOutlinePerson,
  MdOutlineTimer,
  MdSearch,
  MdTaskAlt,
} from 'react-icons/md';

import { useContentRoute } from '../../hooks/use-content-route';
import { RemindTeachersDialog } from './remind-teachers-dialog';
import { ReviewActionButton } from './review-action-button';
import { ReviewDateFilter } from './review-date-filter';
import { ReviewFilter } from './review-filter';
import { ReviewMissingRow } from './review-missing-row';
import { ReviewQueueRow } from './review-queue-row';
import { ReviewReminderDialog } from './review-reminder-dialog';
import type { TReviewDateField, TReviewListFilterKey, TReviewSearch } from './review-search';
import { ReviewStatusTiles, type TReviewView } from './review-status-tiles';
import { TeacherReviewPageSkeleton } from './teacher-review-page-skeleton';
import { useReviewPageState } from './use-review-page-state';

/** The view's part of the export file name. */
const EXPORT_NAMES: Record<TReviewView, string> = {
  [EReviewSubmissionStatus.waiting]: 'waiting',
  [EReviewSubmissionStatus.in_progress]: 'drafts',
  [EReviewSubmissionStatus.reviewed]: 'reviewed',
  [EReviewSubmissionStatus.rejected]: 'rejected',
  all: 'all-submissions',
  not_submitted: 'not-submitted',
};

const SUBMISSION_DATE_FIELDS: Array<{ value: TReviewDateField; label: string }> = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'reviewed', label: 'Feedback sent' },
  { value: 'rejected', label: 'Rejected' },
];
const MISSING_DATE_FIELDS: Array<{ value: TReviewDateField; label: string }> = [
  { value: 'due', label: 'Due date' },
  { value: 'started', label: 'Draft started' },
];

/** Tiles where submissions still wait on a teacher — the only ones with a review deadline or a reminder. */
const isPendingView = (view: TReviewView) =>
  view === EReviewSubmissionStatus.waiting || view === EReviewSubmissionStatus.in_progress || view === 'all';

const EMPTY_MESSAGES: Record<TReviewView, { title: string; body: string }> = {
  [EReviewSubmissionStatus.waiting]: {
    title: 'All caught up',
    body: 'No submissions are waiting for feedback. New ones show up here as students submit.',
  },
  [EReviewSubmissionStatus.in_progress]: {
    title: 'No drafts',
    body: 'Feedback you start but don’t send yet is saved here.',
  },
  [EReviewSubmissionStatus.reviewed]: {
    title: 'Nothing reviewed yet',
    body: 'Submissions you send feedback on show here.',
  },
  [EReviewSubmissionStatus.rejected]: {
    title: 'No rejected submissions',
    body: 'Submissions you send back to the student show here.',
  },
  all: { title: 'No submissions yet', body: 'Your students’ Hands-On Practice submissions show here.' },
  not_submitted: {
    title: 'Everyone has submitted',
    body: 'Every student has sent in every Hands-On Practice form in this space.',
  },
};

/** A form reminder's state: sent back, and how its due date stands. */
const formStatus = (r: TReviewReminderRecipient): string => {
  if (!r.form) {
    return '';
  }
  const due =
    r.form.due === EReviewDueState.overdue
      ? 'overdue'
      : r.form.dueAt
        ? `due ${DateTime.fromJSDate(new Date(r.form.dueAt)).toLocaleString({ day: 'numeric', month: 'short' })}`
        : 'no due date';
  const sentBack = r.form.progress === EReviewMissingProgress.rejected ? 'sent back' : null;
  return [sentBack, due].filter(Boolean).join(' · ');
};

type TReminderTarget =
  | { kind: 'teachers' }
  | { kind: 'students'; student?: TReviewMissingItem; form?: TReviewMissingForm };

interface TeacherReviewPageProps {
  /** The page's state in its URL. */
  search: TReviewSearch;
  onSearchChange: (search: TReviewSearch) => void;
}

/**
 * Teacher Review sub-space: the teacher's queue of their students' Hands-On
 * Practice submissions, and the students who have not submitted yet. Admins see
 * every teacher's and can remind teachers; teachers and admins remind students.
 * Every view filters by date and exports, as filtered, to CSV. The state lives in
 * the URL, so a reminder link opens the same list.
 */
export const TeacherReviewPage: FC<TeacherReviewPageProps> = ({ search: urlSearch, onSearchChange }) => {
  const { programSlug, subSpaceId = '', subSpaceSlug = '' } = useContentRoute();
  const navigate = useNavigate();
  const state = useReviewPageState(urlSearch, onSearchChange);
  const { view, isMissingView, filters, setFilter, setFilters, queueParams, missingParams } = state;
  const [openFilter, setOpenFilter] = useState<TReviewListFilterKey | 'date' | null>(null);
  const [reminder, setReminder] = useState<TReminderTarget | null>(null);
  const studentTarget = reminder?.kind === 'students' ? reminder : null;
  const listTopRef = useRef<HTMLDivElement>(null);

  // A link opens with picked ids but no names; fetch the options once to name them.
  const needsLabels = (Object.keys(filters) as TReviewListFilterKey[]).some(key =>
    filters[key].ids.some(id => !filters[key].labels[id]),
  );
  const { data: space } = useGetSpace(subSpaceId);
  const queue = useGetReviewQueue(subSpaceId, queueParams);
  const missing = useGetReviewMissing(subSpaceId, missingParams, isMissingView);
  const { data } = queue;
  // Counts per value are only worth fetching while a filter panel is open.
  const wantFacets = (openFilter !== null && openFilter !== 'date') || needsLabels;
  const facets = useGetReviewFacets(subSpaceId, queueParams, wantFacets && !isMissingView);
  const missingFacets = useGetReviewMissingFacets(subSpaceId, missingParams, wantFacets && isMissingView);
  const activeFacets = isMissingView ? missingFacets : facets;
  const exportReview = useDownloadReviewExport(subSpaceId);
  const remindStudents = useRemindReviewStudents(subSpaceId);

  const optionsFor = (key: TReviewListFilterKey): TReviewFacetOption[] | undefined => {
    const byKey: Record<TReviewListFilterKey, TReviewFacetOption[] | null | undefined> = {
      module: activeFacets.data?.modules,
      form: activeFacets.data?.forms,
      teacher: activeFacets.data?.teachers,
      reviewTime: facets.data?.reviewTime,
      progress: missingFacets.data?.progress,
      due: missingFacets.data?.due,
    };
    return byKey[key] ?? undefined;
  };

  useEffect(() => {
    if (!needsLabels || !activeFacets.data) {
      return;
    }
    setFilters(prev => {
      const next = { ...prev };
      for (const key of Object.keys(prev) as TReviewListFilterKey[]) {
        const options = optionsFor(key) ?? [];
        const labels = { ...prev[key].labels };
        for (const id of prev[key].ids) {
          labels[id] = labels[id] ?? options.find(o => o.id === id)?.label ?? id;
        }
        next[key] = { ...prev[key], labels };
      }
      return next;
    });
    // Only when options arrive for picks without names.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsLabels, activeFacets.data]);

  const filterProps = (key: TReviewListFilterKey) => ({
    value: filters[key],
    onChange: setFilter(key),
    options: optionsFor(key),
    open: openFilter === key,
    onOpenChange: (isOpen: boolean) => setOpenFilter(isOpen ? key : null),
    isLoading: activeFacets.isFetching,
  });

  const goToPage = (next: number) => {
    state.setPage(next);
    // A new page starts at the top of the list, not wherever the old one was scrolled to.
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openSubmission = (submissionId: string) =>
    navigate({
      to: '/programs/$programSlug/$moduleSlug/review/$submissionId',
      params: { programSlug, moduleSlug: subSpaceSlug, submissionId },
    });

  // The list on screen: submissions, or students who have not submitted.
  const list = isMissingView ? missing : queue;
  const meta = list.data?.meta;
  const nouns = isMissingView ? (['student', 'students'] as const) : (['submission', 'submissions'] as const);
  // A Not submitted file has a row per student and form, so it counts forms.
  const exportRows = isMissingView ? missing.data?.counts.forms : data?.meta.total;
  const isManager = !!data?.isManager;
  const canRemindTeachers = isManager && isPendingView(view);

  const handleExport = async () => {
    const filename = [programSlug, subSpaceSlug, EXPORT_NAMES[view], DateTime.now().toISODate()]
      .filter(Boolean)
      .join('-');
    try {
      await exportReview.mutateAsync({ params: state.exportParams, filename });
      const rows = exportRows ?? 0;
      if (rows > REVIEW_EXPORT_MAX_ROWS) {
        enqueueSnackbar(`Exported the first ${REVIEW_EXPORT_MAX_ROWS} of ${rows} rows. Add a filter to get the rest.`, {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar(`Exported ${rows} ${rows === 1 ? 'row' : 'rows'}`, { variant: 'success' });
      }
    } catch {
      enqueueSnackbar('Export failed. Try again.', { variant: 'error' });
    }
  };

  // The hook keeps the previous page on screen so the tile counts don't flash, but
  // the list itself belongs to the old tab — show it as loading until the new one lands.
  const isListLoading = list.isLoading || list.isPlaceholderData;
  const empty = EMPTY_MESSAGES[view];
  const isCaughtUp = (view === EReviewSubmissionStatus.waiting || isMissingView) && !state.hasActiveFilters;
  const isListEmpty = isMissingView ? !missing.data?.items.length : !data?.items.length;

  return (
    <Box sx={{ pb: 4 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(space?.name ?? 'Teacher Review') }} />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isManager
          ? 'Every teacher’s Hands-On Practice submissions in this program.'
          : 'Your students’ Hands-On Practice submissions. Open one to read it and share your feedback.'}
      </Typography>

      <ReviewStatusTiles
        value={view}
        counts={data?.counts}
        overdueReviews={data?.overdueReviews}
        notSubmitted={data?.notSubmitted}
        onChange={state.setView}
      />

      <Box ref={listTopRef} sx={{ scrollMarginTop: 96 }} />
      <Paper
        variant="outlined"
        aria-busy={isListLoading}
        sx={{ mt: 2.5, borderRadius: '12px', overflow: 'hidden', bgcolor: 'common.white', position: 'relative' }}
      >
        {list.isPlaceholderData && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 1 }} />
        )}
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 1.5, pb: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
          {/* On a phone the search takes the full row and the actions sit under it. */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by student name or email"
              value={state.searchInput}
              onChange={e => state.setSearchInput(e.target.value)}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' }, minWidth: 0 }}
              inputProps={{ 'aria-label': 'Search by student name or email' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdSearch size={18} />
                  </InputAdornment>
                ),
              }}
            />
            {canRemindTeachers && (
              <ReviewActionButton
                label="Remind teachers"
                icon={<MdNotificationsActive />}
                onClick={() => setReminder({ kind: 'teachers' })}
                disabled={!data?.meta.total}
              />
            )}
            {isMissingView && (
              <ReviewActionButton
                label="Remind students"
                icon={<MdNotificationsActive />}
                onClick={() => setReminder({ kind: 'students' })}
                disabled={!missing.data?.meta.total}
              />
            )}
            {/* Exactly what the list shows — this tile, its filters, the search — on every page. */}
            <ReviewActionButton
              label="Export CSV"
              icon={<MdFileDownload />}
              onClick={handleExport}
              disabled={!exportRows}
              isLoading={exportReview.isPending}
            />
            <ActionIconButton
              tooltip="Clear all filters"
              icon={<MdFilterAltOff size={18} />}
              onClick={state.clearFilters}
              disabled={!state.hasActiveFilters}
            />
          </Box>
          {/* Wraps instead of scrolling sideways, so no filter is ever hidden off-screen. */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1.25 }}>
            <ReviewFilter label="Module" noun="modules" icon={<MdOutlineFolder />} {...filterProps('module')} />
            <ReviewFilter label="Form" noun="forms" icon={<MdOutlineAssignment />} {...filterProps('form')} />
            {isManager && (
              <ReviewFilter
                label="Teacher"
                noun="teachers"
                icon={<MdOutlinePerson />}
                showAvatars
                {...filterProps('teacher')}
              />
            )}
            {!isMissingView && isPendingView(view) && (
              <ReviewFilter
                label="Review time"
                noun="review times"
                icon={<MdOutlineTimer />}
                {...filterProps('reviewTime')}
              />
            )}
            {isMissingView && (
              <>
                <ReviewFilter label="Due" noun="due states" icon={<MdOutlineEventBusy />} {...filterProps('due')} />
                <ReviewFilter
                  label="Progress"
                  noun="progress states"
                  icon={<MdOutlineFlag />}
                  {...filterProps('progress')}
                />
              </>
            )}
            <ReviewDateFilter
              fields={isMissingView ? MISSING_DATE_FIELDS : SUBMISSION_DATE_FIELDS}
              value={state.date}
              onChange={state.setDate}
              open={openFilter === 'date'}
              onOpenChange={isOpen => setOpenFilter(isOpen ? 'date' : null)}
            />
            {meta && (
              <Typography
                variant="caption"
                color="text.secondary"
                role="status"
                aria-live="polite"
                sx={{ ml: 'auto', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              >
                {meta.total} {meta.total === 1 ? nouns[0] : nouns[1]}
              </Typography>
            )}
          </Box>
        </Box>

        {list.error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {list.error.message}
          </Alert>
        ) : isListLoading ? (
          <TeacherReviewPageSkeleton />
        ) : data && data.forms.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No forms have been set up for review in this space yet.
          </Alert>
        ) : isListEmpty ? (
          <Box sx={{ py: 7, px: 3, textAlign: 'center' }}>
            <Box sx={{ color: isCaughtUp ? '#2e7d32' : 'text.disabled', mb: 1 }}>
              {isCaughtUp ? <MdTaskAlt size={40} /> : <MdOutlineInbox size={40} />}
            </Box>
            <Typography sx={{ fontWeight: 700 }}>{state.hasActiveFilters ? 'No matches' : empty.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380, mx: 'auto', mt: 0.5 }}>
              {state.hasActiveFilters ? `No ${nouns[1]} match these filters.` : empty.body}
            </Typography>
          </Box>
        ) : isMissingView ? (
          <Box role="list" aria-label="Students who have not submitted">
            {missing.data?.items.map(item => (
              <ReviewMissingRow
                key={item.student.userUuid}
                item={item}
                showTeacher={isManager}
                onRemind={(student, form) => setReminder({ kind: 'students', student, form })}
              />
            ))}
          </Box>
        ) : (
          data?.items.map(item => (
            <ReviewQueueRow key={item.submissionId} item={item} showReviewer={isManager} onOpen={openSubmission} />
          ))
        )}
      </Paper>

      {meta && meta.totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mt: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            Showing {(meta.page - 1) * meta.perPage + 1}–{Math.min(meta.page * meta.perPage, meta.total)} of{' '}
            {meta.total}
          </Typography>
          <Pagination meta={meta} onNext={goToPage} onPrev={goToPage} />
        </Box>
      )}

      <RemindTeachersDialog
        open={reminder?.kind === 'teachers'}
        onClose={() => setReminder(null)}
        spaceId={subSpaceId}
        forms={data?.forms ?? []}
        initialFormIds={filters.form.mode === 'include' ? filters.form.ids : []}
        initialReviewTime={filters.reviewTime.mode === 'include' ? (filters.reviewTime.ids as TReviewTimeState[]) : []}
      />
      <ReviewReminderDialog
        open={reminder?.kind === 'students'}
        onClose={() => setReminder(null)}
        mode={studentTarget?.student ? 'student-forms' : 'students'}
        title={studentTarget?.student ? `Remind ${studentTarget.student.student.name}` : 'Remind students'}
        intro="Each reminder is about one form: the student gets an in-app message and an email that open that form, so they land on exactly what to submit."
        label={r =>
          studentTarget?.student
            ? { primary: r.form?.formTitle ?? '', secondary: formStatus(r) }
            : { primary: r.person.name, secondary: [r.person.email, formStatus(r)].filter(Boolean).join(' · ') }
        }
        // A form chip opens the student's list with only that form ticked; the bell ticks them all.
        preselect={studentTarget?.form ? r => r.form?.formId === studentTarget.form?.formId : undefined}
        preview={() =>
          remindStudents.mutateAsync({
            filters: missingParams,
            studentUserUuids: studentTarget?.student ? [studentTarget.student.student.userUuid] : undefined,
            dryRun: true,
          })
        }
        send={(picked, note) =>
          remindStudents.mutateAsync({
            filters: missingParams,
            studentUserUuids: studentTarget?.student ? [studentTarget.student.student.userUuid] : undefined,
            targets: picked.flatMap(r =>
              r.form ? [{ studentUserUuid: r.person.userUuid, formId: r.form.formId }] : [],
            ),
            note,
            dryRun: false,
          })
        }
      />
    </Box>
  );
};
